/* Homepage v2 tile behaviour — preview only.
 *
 * Three things:
 *   1. desktop hover  -> that tile loads and plays
 *   2. touch          -> ONE visible tile at a time plays, on a rotation
 *   3. press          -> overlay with the product and a Discover button
 *
 * ---- THE RULE THAT KEEPS THIS CHEAP ----
 * Above the fold, two clips play on their own (see 0b, "ambient"); anywhere
 * else at most one video is ever playing, and a video is never fetched until
 * it is asked to play. The markup ships preload="none" with the URL parked in
 * data-src, so a page of a dozen tiles costs a dozen poster images and no
 * video bytes at all until someone interacts.
 *
 * ---- WHY NOT AUTOPLAY EVERYTHING ----
 * Twelve simultaneous loops is tens of megabytes and a hot phone, and iOS
 * refuses more than a couple of concurrent decodes anyway - tiles would drop
 * out at random and look broken. The rotation gives the same impression of a
 * living page for the cost of one video.
 */
(function () {
  'use strict';

  if (window.__ejhHome) return;
  window.__ejhHome = true;

  var root = document.querySelector('[data-ejh-root]');
  if (!root) return;

  var tiles = Array.prototype.slice.call(root.querySelectorAll('[data-ejh-tile]'));
  if (!tiles.length) return;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* A real pointer, not a touch screen emulating one. Hover on touch fires on
     tap and would fight the rotation. */
  var hasPointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var playing = null;

  function video(tile) { return tile.querySelector('.ejh-tile__video'); }

  function play(tile, concurrent) {
    if (!tile || reduced) return;
    var v = video(tile);
    if (!v) return;                      /* stills-only product: nothing to do */
    if (!concurrent && playing && playing !== tile) stop(playing);

    /* Attach the source on first use. Re-attaching on every hover would
       restart the download each time. */
    if (!v.src) {
      var src = v.getAttribute('data-src');
      if (!src) return;
      window.ejhSetSrc ? ejhSetSrc(v, src) : (v.src = src);
    }
    playing = tile;
    measureDrop(tile);
    /* An ambient clip runs unlooped so its end can hand over to the next
       tile; a plain hover loops as before. */
    if (!tile.classList.contains('is-ambient')) v.loop = true;
    var p = v.play();
    /* A rejected play() is normal - autoplay policy, or the element was
       stopped before the promise settled. It must not throw into the console
       on every hover.

       One rejection is worth acting on: NotAllowedError. Safari in Low Power
       Mode (a MacBook on battery, any iPhone) refuses even a muted play()
       that did not come from a tap or a key, and a hover is neither - the
       tile then sits on its poster looking stuck. The first real gesture
       anywhere on the page unlocks it, so the play is retried on that
       gesture for whichever tile is hovered by then. */
    if (p && p.catch) p.catch(function (err) {
      if (!err || err.name !== 'NotAllowedError') return;
      retryOnGesture();
    });
    tile.classList.add('is-playing');
  }

  var gestureArmed = false;
  function retryOnGesture() {
    if (gestureArmed) return;
    gestureArmed = true;
    var events = ['pointerdown', 'touchstart', 'keydown'];
    function unlock() {
      events.forEach(function (e) { document.removeEventListener(e, unlock, true); });
      gestureArmed = false;
      if (!playing) return;
      var v = video(playing);
      if (!v) return;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
    events.forEach(function (e) { document.addEventListener(e, unlock, true); });
  }

  /* How far the FOR HIM label has to travel to sit just above the hover
     paragraph: from its own bottom edge to the paragraph's top, less a
     breath. offsetTop is layout, untouched by any transform mid-flight, and
     both are measured against the same positioned ancestor (the meta block),
     so a re-hover during the animation still measures true. */
  function measureDrop(tile) {
    var eyebrow = tile.querySelector('.ejh-tile__eyebrow');
    var hover = tile.querySelector('.ejh-tile__hover');
    var swap = tile.querySelector('.ejh-tile__swap');
    if (!eyebrow || !hover || !swap) return;
    /* The paragraph is absolutely positioned at the bottom of the swap slot,
       so its top in the meta block is the slot's bottom less its own height.
       (offsetTop on the paragraph itself would be relative to the slot.) */
    var hoverTop = swap.offsetTop + swap.offsetHeight - hover.offsetHeight;
    var drop = hoverTop - (eyebrow.offsetTop + eyebrow.offsetHeight) - 8;
    /* Negative is allowed: a paragraph TALLER than the headline slot (the
       strip tiles, three lines of copy over a two-line slot) rises above
       it, and the label moves UP to stay clear of it. Clamping at zero left
       the label sitting on the paragraph's first line - Rudi: "the mouse
       over paragraph trims off." */
    tile.style.setProperty('--ejh-drop', drop + 'px');
  }

  function stop(tile) {
    if (!tile) return;
    var v = video(tile);
    tile.classList.remove('is-playing');
    /* An ambient tile only loses its overlay treatment on mouse-out; the
       clip itself belongs to the ambient rotation and runs to its end. */
    if (tile.classList.contains('is-ambient')) { if (playing === tile) playing = null; return; }
    /* Pause AND rewind, so the next hover starts the clip from its first
       frame - the poster - rather than wherever the last one left off. The
       rewind waits for the fade-out so the jump back is never seen. */
    if (v) {
      try { v.pause(); } catch (e) {}
      window.setTimeout(function () {
        if (tile.classList.contains('is-playing')) return;
        try { v.currentTime = 0; } catch (e) {}
      }, 380);
    }
    if (playing === tile) playing = null;
  }

  /* ---- 0. Warm the heroes ----
     The two hero clips are the first thing anyone hovers, and the first
     hover used to pay for the whole download - a beat on the poster before
     motion, longer on a slow line, which read as "stuck". Once the page has
     loaded and settled, everything above the fold - the two heroes and the
     strip tile beneath them - gets its source attached with preload=auto,
     so the browser fetches them in the background at its own pace, all
     480p transcodes. Everything below stays lazy. Skipped when the visitor
     has asked for data saving, and on touch, where the rotation already
     fetches one clip at a time. */
  function warmHeroes() {
    if (reduced) return;
    var conn = navigator.connection;
    if (conn && conn.saveData) return;
    /* The heroes and the strip directly beneath them: everything above the
       fold, so everything a visitor reaches first. Each strip clip is a 480p
       transcode of a ten-second film, about 1.5 MB. */
    var strip = document.querySelector('.ejh-band--flush + .ejh-band');
    tiles.forEach(function (tile) {
      var hero = tile.classList.contains('ejh-tile--headline');
      var inStrip = strip && strip.contains(tile);
      if (!hero && !inStrip) return;
      var v = video(tile);
      if (!v || v.src) return;
      var src = v.getAttribute('data-src');
      if (!src) return;
      v.preload = 'auto';
      window.ejhSetSrc ? ejhSetSrc(v, src) : (v.src = src);
      v.load();
    });
  }
  if (hasPointer) {
    if (document.readyState === 'complete') window.setTimeout(warmHeroes, 800);
    else window.addEventListener('load', function () { window.setTimeout(warmHeroes, 800); });
  }

  /* ---- 0b. Ambient play ----
     Rudi: "out of all these six blocks I want you to randomly play by itself
     two videos without changing the overlay content unless the person mouse
     overs. When a video finishes a different new one starts to play. The two
     videos that play at the same time can never be next to each other. It
     should always feel very random and balanced."

     The six above-the-fold tiles - two heroes, four strip tiles. Two of them
     are always "ambient": the clip fades in and plays through with the
     resting overlay (label, headline, name, price) untouched. When a clip
     ends its tile falls back to the poster and, after a short random beat,
     a different tile takes over. Adjacency is geometric - two tiles are
     neighbours when they share an edge, not just a corner - so it holds at
     any width and on the stacked mobile layout. Balance comes from
     preferring the tiles that have been quiet longest: the last three to
     finish are excluded while there is any other choice.

     Hover still works exactly as before: an ambient tile that is hovered
     gains the overlay treatment on top of its running clip (and loops until
     the mouse leaves, then finishes normally); hovering any other tile
     plays it as a third clip, and the rotation steers clear of it and its
     neighbours. */
  var ambientTiles = [];
  var ambient = [];          /* the (up to two) tiles currently ambient */
  var ambientHistory = [];   /* handles of the last tiles to finish, newest last */
  /* Rudi: "I see you playing the same videos over and over you should
     randomise all of them so that all the videos play at random times."
     A shuffled deck: every tile is dealt once, in random order, before the
     deck is reshuffled - so no clip repeats until all six have had a turn.
     A tile the adjacency rule forbids right now stays in the deck for the
     next draw rather than being skipped. */
  var ambientDeck = [];
  function shuffle(a) {
    for (var i = a.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)); var t = a[i]; a[i] = a[j]; a[j] = t; }
    return a;
  }
  function dealFrom(pool) {
    for (var i = 0; i < ambientDeck.length; i++) {
      if (pool.indexOf(ambientDeck[i]) !== -1) return ambientDeck.splice(i, 1)[0];
    }
    return null;
  }
  (function () {
    var strip = document.querySelector('.ejh-band--flush + .ejh-band');
    tiles.forEach(function (tile) {
      /* Variant C drives the two hero clips itself -- one plays, the other
         waits its turn -- so the ambient rotation must keep its hands off
         them or the two fight over the same <video>. */
      if (tile.closest && tile.closest('[data-ejh-carousel]')) return;
      var hero = tile.classList.contains('ejh-tile--headline');
      var inStrip = strip && strip.contains(tile);
      if ((hero || inStrip) && video(tile)) ambientTiles.push(tile);
    });
  })();

  /* Variant B's "For them" answer hands the two hero clips to the carousel
     at RUNTIME, long after this rotation has claimed them. Marking the band
     in Liquid the way template C does is not an option there -- on a For him
     or For her answer the heroes stay in the rotation and must keep playing
     -- so the carousel asks for them instead. */
  window.ejhReleaseTiles = function (nodes) {
    Array.prototype.forEach.call(nodes || [], function (n) {
      var i = ambientTiles.indexOf(n);
      if (i !== -1) { ambientTiles.splice(i, 1); }
      /* Out of the live set too, and stripped of the rotation's own classes.
         Leaving is-ambient on a released tile lets the rotation's delayed
         housekeeping believe it is still in charge of it. */
      var j = ambient.indexOf(n);
      if (j !== -1) { ambient.splice(j, 1); }
      n.classList.remove('is-playing', 'is-ambient', 'is-ambient--live');
      var v = video(n);
      if (v) { try { v.pause(); } catch (e) {} }
    });
  };

  /* ------------------------------------------------------------------
     THE DISCOUNT BAR MUST BE ON SCREEN WHEN THEY LAND

     Rudi: "the discount bar should ALWAYS be visible at the bottom of the
     screen when they first land, so the banners must auto resize on all the
     homepages."

     The first attempt did this arithmetically -- viewport minus bar minus
     strip, split 58/42 -- and it was still short, because the arithmetic has
     to know about every margin, gap and border between the bands, and it
     did not. So this measures instead: render, ask where the offer strip
     actually ends, and take the overflow off the two rows of banners in
     proportion to their height. Anything the arithmetic would have had to
     predict is already baked into the number it reads back.

     It only ever SHRINKS. If the strip is on screen there is nothing to do,
     and growing banners to fill a tall screen is a different decision from
     the one being asked for.

     Works on all three homepages by finding the rows rather than being told:
     variant B's lead group if it is there, otherwise the hero band and the
     row of small tiles, which is also what the carousel leaves behind.
     ------------------------------------------------------------------ */
  /* The fold fit lives in ejh-fold.js, inlined by the section so it runs
     before first paint; window.ejhFitFold is its scheduler. */

  /* ---- Headlines that fit the slot they are given -------------------
     .ejh-tile__swap is a fixed two-line box, on purpose: it is what keeps
     every FOR HIM label, name and price on the same row across tiles of
     different headline lengths. The comment on it says a third line "simply
     rises above the slot" -- which turns out to mean it lands on top of the
     product name and the price. Rudi saw exactly that on V1, where the two
     heroes are half-width and a headline that fits on two lines full-width
     runs to three.

     Rather than let the copy be constrained by the box, the box shrinks the
     copy: measure the headline against its slot and step the size down until
     it fits. Nothing to keep in step when the copy changes again, and it is
     the same answer at every screen width.

     Only when it overflows, so a headline that already fits is untouched and
     renders at exactly the size the stylesheet asked for. On a phone the
     slot is height:auto, so nothing ever overflows and this does nothing. */
  var HL_FLOOR = 0.55;  /* never smaller than this share of the CSS size */
  function fitHeadline(hl) {
    var swap = hl.parentNode;
    if (!swap) { return; }
    hl.style.fontSize = '';
    var base = parseFloat(window.getComputedStyle(hl).fontSize);
    if (!base) { return; }
    var size = base, guard = 0;

    /* On a phone the slot is height:auto, so the headline can never overflow
       IT -- but the tile is fold-fitted down to 200px and the text block is
       anchored to its bottom, so a tall stack climbs out of the top of the
       tile. Rudi: "text cut off". The measure that matters there is the
       top of the text block against the top of the tile; shrink the
       headline until the block sits inside, with a breath to spare. */
    var phone = !window.matchMedia('(min-width: 750px)').matches;
    if (phone) {
      var tile = hl.closest('.ejh-tile');
      var meta = tile && tile.querySelector('.ejh-tile__meta');
      var top = meta && meta.firstElementChild;
      if (!tile || !top) { return; }
      var floor = tile.getBoundingClientRect().top + 10;
      while (top.getBoundingClientRect().top < floor && guard++ < 16) {
        size = size * 0.96;
        if (size < base * HL_FLOOR) { break; }
        hl.style.fontSize = size.toFixed(2) + 'px';
      }
      return;
    }

    var box = swap.clientHeight;
    if (!box) { return; }
    while (hl.getBoundingClientRect().height > box + 1 && guard++ < 16) {
      size = size * 0.96;
      if (size < base * HL_FLOOR) { break; }
      hl.style.fontSize = size.toFixed(2) + 'px';
    }
  }
  function fitHeadlines() {
    var hls = document.querySelectorAll('.ejh-tile__headline');
    for (var i = 0; i < hls.length; i++) { fitHeadline(hls[i]); }
  }
  var hlTimer = null;
  function scheduleHeadlines() {
    window.clearTimeout(hlTimer);
    hlTimer = window.setTimeout(fitHeadlines, 120);
  }
  /* Variant B rebuilds the band -- half-width heroes become full width -- so
     it re-runs this once the new layout has been laid out. */
  window.ejhFitHeadlines = scheduleHeadlines;
  window.addEventListener('resize', scheduleHeadlines);
  window.addEventListener('load', scheduleHeadlines);
  if (document.readyState === 'complete') { scheduleHeadlines(); }
  /* Web fonts land after first paint and change every measurement above. */
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(scheduleHeadlines);
  }


  function adjacent(a, b) {
    var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
    var overlapX = Math.min(ra.right, rb.right) - Math.max(ra.left, rb.left);
    var overlapY = Math.min(ra.bottom, rb.bottom) - Math.max(ra.top, rb.top);
    var gapX = Math.max(ra.left, rb.left) - Math.min(ra.right, rb.right);
    var gapY = Math.max(ra.top, rb.top) - Math.min(ra.bottom, rb.bottom);
    /* Share a vertical edge (side by side) or a horizontal edge (stacked).
       A corner touch has no overlap on the other axis and does not count. */
    return (overlapY > 8 && gapX <= 6) || (overlapX > 8 && gapY <= 6);
  }

  function pickAmbient() {
    var busy = ambient.slice();
    if (playing && busy.indexOf(playing) === -1) busy.push(playing);
    var base = ambientTiles.filter(function (t) {
      if (busy.indexOf(t) !== -1) return false;
      for (var i = 0; i < busy.length; i++) if (adjacent(t, busy[i])) return false;
      return true;
    });
    if (!base.length) return null;
    var pick = dealFrom(base);
    if (pick) return pick;
    /* Deck exhausted (or only holds tiles the rule forbids right now):
       reshuffle everything that is not busy, avoiding the most recent
       finisher when there is any other choice, and deal again. */
    var last = ambientHistory.length ? ambientHistory[ambientHistory.length - 1] : null;
    var fresh = ambientTiles.filter(function (t) { return busy.indexOf(t) === -1; });
    var held = ambientDeck.slice();
    ambientDeck = shuffle(fresh.filter(function (t) { return held.indexOf(t) === -1; })).concat(held);
    var noRepeat = base.filter(function (t) { return t.getAttribute('data-handle') !== last; });
    return dealFrom(noRepeat.length ? noRepeat : base);
  }

  function ambientEnded(tile) {
    var v = video(tile);
    if (tile.classList.contains('is-playing')) {
      /* Still under the mouse: behave like a loop until it leaves. */
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
      return;
    }
    finishAmbient(tile);
    window.setTimeout(fillAmbient, 400 + Math.random() * 900);
  }

  function finishAmbient(tile) {
    var i = ambient.indexOf(tile);
    if (i !== -1) ambient.splice(i, 1);
    tile.classList.remove('is-ambient', 'is-ambient--live');
    var h = tile.getAttribute('data-handle');
    ambientHistory = ambientHistory.filter(function (x) { return x !== h; });
    ambientHistory.push(h);
    if (ambientHistory.length > 3) ambientHistory.shift();
    var v = video(tile);
    window.setTimeout(function () {
      if (tile.classList.contains('is-playing') || tile.classList.contains('is-ambient')) return;
      try { v.pause(); v.currentTime = 0; } catch (e) {}
    }, 380);
  }

  /* Rudi: "ONLY play the videos after they have loaded successfully in the
     background." A clip is ready when the whole file is buffered - not just
     enough to start - so an ambient tile never stutters or shows a spinner.
     The slot is reserved while the download finishes; if it fails or takes
     too long the slot is released and another tile is chosen. */
  function fullyBuffered(v) {
    /* Either the whole file is in the buffer, or the browser has declared
       HAVE_ENOUGH_DATA - its own promise that the clip will play through
       without stalling. Chrome throttles background buffering of a paused
       video (probed: a hero parked at 85% for half a minute), so the
       buffer test alone never fires for it. */
    if (v.readyState >= 4) return true;
    if (!(v.duration > 0) || !v.buffered || !v.buffered.length) return false;
    return v.buffered.end(v.buffered.length - 1) >= v.duration - 0.25;
  }

  function startAmbient(tile) {
    var v = video(tile);
    if (!v.src) { var src = v.getAttribute('data-src'); if (!src) return false; v.preload = 'auto'; window.ejhSetSrc ? ejhSetSrc(v, src) : (v.src = src); v.load(); }
    if (!v.__ejhAmbientBound) {
      v.__ejhAmbientBound = true;
      v.addEventListener('ended', function () { if (tile.classList.contains('is-ambient')) ambientEnded(tile); });
    }
    ambient.push(tile);
    tile.classList.add('is-ambient');
    if (fullyBuffered(v)) { runAmbient(tile); return true; }

    v.preload = 'auto';
    var settled = false;
    function done() {
      if (settled) return;
      settled = true;
      ['progress', 'canplaythrough', 'loadeddata', 'error', 'stalled'].forEach(function (e) { v.removeEventListener(e, check); });
      window.clearTimeout(timer);
    }
    function check(e) {
      if (!tile.classList.contains('is-ambient')) { done(); return; }
      if (e && e.type === 'error') { done(); giveUp(); return; }
      if (fullyBuffered(v)) { done(); runAmbient(tile); }
    }
    function giveUp() {
      finishAmbient(tile);
      window.setTimeout(fillAmbient, 600);
    }
    var timer = window.setTimeout(function () { done(); if (tile.classList.contains('is-ambient') && !fullyBuffered(v)) giveUp(); else if (tile.classList.contains('is-ambient')) runAmbient(tile); }, 25000);
    ['progress', 'canplaythrough', 'loadeddata', 'error', 'stalled'].forEach(function (e) { v.addEventListener(e, check); });
    if (v.readyState < 1) { try { v.load(); } catch (e) {} }
    return true;
  }

  function runAmbient(tile) {
    var v = video(tile);
    v.loop = false;
    /* If the tile was hovered while its file finished downloading the clip
       is already running for the hover; do not yank it back to the start. */
    if (!tile.classList.contains('is-playing')) { try { v.currentTime = 0; } catch (e) {} }
    tile.classList.add('is-ambient--live');
    var p = v.play();
    if (p && p.catch) p.catch(function (err) {
      /* Autoplay refused (Low Power Mode): give the slot back and wait for
         the first gesture on the page before trying the rotation again. */
      finishAmbient(tile);
      if (err && err.name === 'NotAllowedError') armAmbientGesture();
    });
    return true;
  }

  var ambientGestureArmed = false;
  function armAmbientGesture() {
    if (ambientGestureArmed) return;
    ambientGestureArmed = true;
    var events = ['pointerdown', 'touchstart', 'keydown'];
    function unlock() {
      events.forEach(function (e) { document.removeEventListener(e, unlock, true); });
      ambientGestureArmed = false;
      fillAmbient();
    }
    events.forEach(function (e) { document.addEventListener(e, unlock, true); });
  }

  function fillAmbient() {
    if (reduced || document.hidden || ambientGestureArmed) return;
    var guard = 0;
    while (ambient.length < 2 && guard++ < 4) {
      var next = pickAmbient();
      if (!next) return;
      if (!startAmbient(next)) return;
    }
  }

  function beginAmbient() {
    if (reduced || ambientTiles.length < 2) return;
    /* Touch has its own rule: every clip runs, each on its own cycle. */
    if (!hasPointer) return;
    var conn = navigator.connection;
    if (conn && conn.saveData) return;
    /* Rudi: "it's fine if it picks its own moment, but make sure they
       autoplay as soon as the page is open."

       So the rotation still chooses what follows, but it no longer opens on
       a 1.4-second pause and one banner: both heroes start immediately, as
       the opening pair. They are side by side and the adjacency rule would
       normally forbid that -- the rule exists to stop two neighbouring clips
       competing -- but the two big banners ARE the top of the page, and a
       visitor landing on a still is the thing being complained about.
       Everything after this pair goes back through the usual rules. */
    var open = function () {
      ambientDeck = shuffle(ambientTiles.slice());
      /* The heroes are the two tiles in the top band - not "tiles with a
         headline", which the strip tiles are too. */
      var heroBand = document.querySelector('[data-ejh-heroband]')
                  || document.querySelector('.ejh-band--flush');
      var heroes = ambientTiles.filter(function (t) { return heroBand && heroBand.contains(t); });

      var seed = heroes.length ? heroes.slice(0, 2) : [];
      if (!seed.length) {
        var one = pickAmbient();
        if (one) { seed = [one]; }
      }
      seed.forEach(function (t) {
        var di = ambientDeck.indexOf(t);
        if (di !== -1) { ambientDeck.splice(di, 1); }
        startAmbient(t);
      });
      /* Only top up if the heroes did not already fill the two slots. */
      window.setTimeout(fillAmbient, 900 + Math.random() * 1200);
    };
    if (document.readyState === 'complete') { open(); }
    else { window.addEventListener('load', open); }
    document.addEventListener('visibilitychange', function () {
      ambient.forEach(function (t) {
        var v = video(t);
        if (document.hidden) { try { v.pause(); } catch (e) {} }
        else { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      });
      if (!document.hidden) fillAmbient();
    });
  }
  if (document.readyState === 'complete') beginAmbient();
  else window.addEventListener('load', beginAmbient);

  /* ---- 1. Hover, desktop only ---- */
  if (hasPointer && !reduced) {
    tiles.forEach(function (tile) {
      /* The hero tiles sit inside a cell with a collection link over their
         corner. Hover has to belong to the cell, or moving the pointer onto
         the link leaves the tile and stops the clip mid-motion. */
      var hoverEl = tile.closest('.ejh-cell') || tile;
      hoverEl.addEventListener('mouseenter', function () { play(tile); });
      hoverEl.addEventListener('mouseleave', function () { stop(tile); });
      /* Keyboard users get the same thing on focus. */
      tile.addEventListener('focus', function () { play(tile); });
      tile.addEventListener('blur', function () { stop(tile); });
    });
  }

  /* ---- 2. Touch: every clip runs, each on its own cycle ----
     Rudi: "play all the videos at the same time as soon as they are loaded.
     When a video is out of view stop it."

     So the fetch is not gated on anything: every tile starts downloading at
     once on load, and each runs the moment it has enough data - no stagger,
     no shared clock, whichever is ready goes. It plays to its end, sits on
     its poster for three seconds and goes again. Nothing loops, because the
     pause has to fall between runs.

     View only governs whether a clip should be running: one that scrolls
     away is paused and its pending restart cancelled, and it picks up again
     when it returns, with no wait, since its data is already here. */
  if (!hasPointer && !reduced && 'IntersectionObserver' in window) {
    var onScreen = [];
    var restarts = [];          /* pending three-second waits, by tile */

    function clearRestart(tile) {
      for (var i = 0; i < restarts.length; i++) {
        if (restarts[i].tile === tile) {
          window.clearTimeout(restarts[i].id);
          restarts.splice(i, 1);
          return;
        }
      }
    }

    function begin(tile) {
      if (onScreen.indexOf(tile) === -1) { return; }
      var v = video(tile);
      if (!v) { return; }
      if (!v.src) { prime(tile); }
      if (v.readyState < 2) {
        v.addEventListener('canplay', function handler() {
          v.removeEventListener('canplay', handler);
          if (onScreen.indexOf(tile) !== -1) { begin(tile); }
        });
        return;
      }
      play(tile, true);
      v.loop = false;           /* play() loops by default; the pause needs an end */
    }

    /* Attach the source and the end-of-run handler once, up front. */
    function prime(tile) {
      var v = video(tile);
      if (!v || v.src) { return; }
      var src = v.getAttribute('data-src');
      if (!src) { return; }
      v.preload = 'auto';
      window.ejhSetSrc ? ejhSetSrc(v, src) : (v.src = src);
      v.addEventListener('ended', function () {
        if (onScreen.indexOf(tile) === -1) { return; }
        tile.classList.remove('is-playing');
        try { v.currentTime = 0; } catch (e) {}
        clearRestart(tile);
        restarts.push({
          tile: tile,
          id: window.setTimeout(function () {
            clearRestart(tile);
            if (onScreen.indexOf(tile) !== -1) { begin(tile); }
          }, 3000)
        });
      });
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var i = onScreen.indexOf(e.target);
        if (e.isIntersecting) {
          if (i === -1) { onScreen.push(e.target); }
          begin(e.target);
        } else {
          if (i !== -1) { onScreen.splice(i, 1); }
          clearRestart(e.target);
          stop(e.target);
        }
      });
    }, { threshold: 0.3 });

    var fetcher = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { prime(e.target); fetcher.unobserve(e.target); }
      });
    }, { rootMargin: '150% 0px' });

    /* Not before the page has loaded. Armed at parse time, the fetcher had
       six clips downloading within 150% of the viewport while the hero
       poster -- the page's largest contentful paint -- was still on its
       way, and Lighthouse put mobile LCP at 12s against the live page's
       9s. The posters are in by the load event; the clips can start then.
       Desktop already waits for load (warmHeroes, beginAmbient). */
    function armTouch() {
      tiles.forEach(function (t) {
        if (!video(t)) { return; }
        fetcher.observe(t);
        io.observe(t);
      });
    }
    if (document.readyState === 'complete') { armTouch(); }
    else { window.addEventListener('load', armTouch); }

    /* A backgrounded tab should not be decoding video. */
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) { return; }
      tiles.forEach(function (t) { stop(t); });
    });
  }

  /* ---- 2b. The discount band's button ----
     The live pop-up is owned by erojoy-main-popup.js, whose open() is closed
     over; the only public way in is the fixed bar's own CTA, which the
     preview keeps mounted but hidden. Clicking it runs the live flow:
     un-dismiss, open the pop-up; closing the pop-up remounts the bar, so
     the band's button works again. */
  var POPUP_KEY = 'ej_main_popup15';
  var reinjected = false;

  function popupState() {
    try { return JSON.parse(localStorage.getItem(POPUP_KEY) || 'null') || {}; }
    catch (e) { return {}; }
  }

  /* Already has the code: the script returns at load and nothing can open,
     so say so rather than leaving the click dead. */
  function alreadyHasCode() {
    if (popupState().done) { return true; }
    try {
      var d = JSON.parse(localStorage.getItem('erojoy_discount') || 'null');
      return !!(d && d.code && (!d.exp || Date.now() < d.exp));
    } catch (e) { return false; }
  }

  /* Un-dismiss exactly the way the bar's own CTA does, so the pop-up will
     agree to show again. A completed sign-up is left alone. */
  function undismiss() {
    var s = popupState();
    if (s.done) { return; }
    delete s.dismissedAt;
    try { localStorage.setItem(POPUP_KEY, JSON.stringify(s)); } catch (e) {}
    try { sessionStorage.removeItem('ej_main_bar_closed'); } catch (e) {}
  }

  /* The pop-up listens for exit intent: a mouseout off the top of the page
     with no relatedTarget. Firing that is the only public way in. */
  function fireExitIntent() {
    var ev;
    try {
      ev = new MouseEvent('mouseout', { bubbles: true, cancelable: true, clientX: 0, clientY: 0 });
    } catch (e) {
      ev = document.createEvent('MouseEvents');
      ev.initMouseEvent('mouseout', true, true, window, 0, 0, 0, 0, 0,
                        false, false, false, false, 0, null);
    }
    document.dispatchEvent(ev);
  }

  function popupOpen() { return !!document.querySelector('.ejp-modal'); }

  /* If the visitor dismissed the pop-up AND closed the bar, the script
     attached no listeners at all this page load. Running a second copy with
     the suppression cleared gives us a live listener to fire at. */
  function reinject(then) {
    if (reinjected) { then(); return; }
    reinjected = true;
    var src = document.querySelector('script[src*="erojoy-main-popup"]');
    if (!src) { then(); return; }
    var el = document.createElement('script');
    el.src = src.getAttribute('src');
    el.onload = then;
    el.onerror = then;
    document.body.appendChild(el);
  }

  Array.prototype.forEach.call(root.querySelectorAll('[data-ejh-offer-cta]'), function (btn) {
    var label = btn.textContent;
    btn.addEventListener('click', function () {
      var cta = document.querySelector('.ejb .ejb-cta');
      if (cta) { cta.click(); return; }
      if (alreadyHasCode()) {
        btn.textContent = 'Your 15% code is already active';
        btn.setAttribute('aria-disabled', 'true');
        window.setTimeout(function () {
          btn.textContent = label;
          btn.removeAttribute('aria-disabled');
        }, 4000);
        return;
      }
      undismiss();
      fireExitIntent();
      window.setTimeout(function () {
        if (popupOpen()) { return; }
        reinject(function () {
          window.setTimeout(function () {
            if (popupOpen()) { return; }
            fireExitIntent();
          }, 60);
        });
      }, 250);
    });
  });

  /* ---- 3. Overlay ---- */
  var overlay = root.querySelector('[data-ejh-overlay]');
  if (!overlay) return;

  var oVideo = overlay.querySelector('.ejh-overlay__video');
  var oImg = overlay.querySelector('.ejh-overlay__img');
  var oTitle = overlay.querySelector('[data-ejh-title]');
  var oBlurb = overlay.querySelector('[data-ejh-blurb]');
  var oPrice = overlay.querySelector('[data-ejh-price]');
  var oEyebrow = overlay.querySelector('[data-ejh-eyebrow]');
  var oCta = overlay.querySelector('[data-ejh-cta]');
  var lastFocused = null;

  function open(tile) {
    stop(playing);
    lastFocused = tile;

    var title = tile.getAttribute('data-title') || '';
    oTitle.textContent = title;
    if (oImg) { oImg.alt = title ? ('Erojoy ' + title) : ''; }
    oBlurb.textContent = tile.getAttribute('data-blurb') || '';
    oPrice.textContent = tile.getAttribute('data-price') ? 'From ' + tile.getAttribute('data-price') : '';
    var eyebrow = tile.querySelector('.ejh-tile__eyebrow');
    oEyebrow.textContent = eyebrow ? eyebrow.textContent : '';
    oCta.setAttribute('href', tile.getAttribute('data-url') || '#');
    oCta.textContent = 'Discover ' + title;

    var poster = tile.querySelector('.ejh-tile__poster');
    oImg.src = poster ? poster.currentSrc || poster.src : '';

    overlay.hidden = false;
    /* Two frames before adding the class: the element has to be laid out at
       its start state or the transition has nothing to move from. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { overlay.classList.add('is-open'); });
    });

    var tv = video(tile);
    if (tv && !reduced) {
      oVideo.__ejhRaw = null;
      window.ejhSetSrc ? ejhSetSrc(oVideo, tv.getAttribute('data-src') || '') : (oVideo.src = tv.getAttribute('data-src') || '');
      var p = oVideo.play();
      if (p && p.catch) p.catch(function () {});
      overlay.classList.add('is-playing');
    } else {
      overlay.classList.remove('is-playing');
      oVideo.removeAttribute('src');
    }

    document.documentElement.style.overflow = 'hidden';
    overlay.querySelector('.ejh-overlay__close').focus();
  }

  function close() {
    overlay.classList.remove('is-open', 'is-playing');
    document.documentElement.style.overflow = '';
    try { oVideo.pause(); } catch (e) {}
    /* Hidden only after the fade, or the panel vanishes instead of leaving. */
    window.setTimeout(function () {
      overlay.hidden = true;
      if (oVideo.__ejhHls) { try { oVideo.__ejhHls.destroy(); } catch (e2) {} oVideo.__ejhHls = null; }
      oVideo.__ejhRaw = null;
      oVideo.removeAttribute('src');   /* stops the download if it was still in flight */
      oImg.removeAttribute('src');
    }, 320);
    if (lastFocused) { try { lastFocused.focus(); } catch (e) {} }
  }

  /* Rudi: "when you click on discover on any product don't do the pop-up,
     just go straight to the product page." The overlay stays in the file,
     unused, in case it is wanted back. */
  tiles.forEach(function (tile) {
    tile.addEventListener('click', function () {
      if (tile.getAttribute('href')) { return; }
      open(tile);
    });
  });

  Array.prototype.forEach.call(overlay.querySelectorAll('[data-ejh-close]'), function (el) {
    el.addEventListener('click', close);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !overlay.hidden) close();
  });
})();

/* ---- THE OPENER PARALLAX ----
   Rudi: "make this banner parallax." The image overhangs its band by 12% top
   and bottom, and is translated against the scroll so it drifts as the band
   crosses the viewport. Cheap: a rAF-coalesced scroll handler that only runs
   while the band is on screen, writing one custom property. Anyone who has
   asked for reduced motion keeps a flat image. */
(function () {
  var bands = document.querySelectorAll('.ejh-opener');
  if (!bands.length) return;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  Array.prototype.forEach.call(bands, function (band) {
    var img = band.querySelector('.ejh-opener__img');
    if (!img) return;
    if (reduce) { band.classList.add('ejh-opener--still'); return; }
    drive(band);
  });

  function drive(band) {
  var visible = false, queued = false;

  function apply() {
    queued = false;
    var r = band.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    /* -1 when the band is just below the fold, +1 when it has just left the top */
    var progress = ((vh - r.top) / (vh + r.height)) * 2 - 1;
    if (progress < -1) progress = -1;
    if (progress > 1) progress = 1;
    /* The image has 22% of the band's height spare at each end; use most of it. */
    var reach = r.height * 0.2;
    band.style.setProperty('--ejh-parallax', (-progress * reach).toFixed(1));
  }

  function onScroll() {
    if (!visible || queued) return;
    queued = true;
    window.requestAnimationFrame(apply);
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible) apply();
    }, { rootMargin: '120px 0px' }).observe(band);
  } else {
    visible = true;
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  apply();
  }
})();

/* The scroll cue: show it a beat after landing, but only if the page is
   actually taller than the window and the visitor has not already scrolled.
   Any scroll retires it for good - it is a hint on arrival, not furniture. */
(function () {
  var cue = document.querySelector('[data-ejh-scrollcue]');
  if (!cue) { return; }
  var done = false;

  function retire() {
    if (done) { return; }
    done = true;
    cue.classList.remove('is-on');
    window.removeEventListener('scroll', onScroll);
    window.setTimeout(function () { if (cue.parentNode) { cue.parentNode.removeChild(cue); } }, 700);
  }

  function onScroll() {
    if (window.pageYOffset > 24) { retire(); }
  }

  cue.addEventListener('click', function () {
    var by = Math.round(window.innerHeight * 0.88);
    window.scrollTo({ top: window.pageYOffset + by, behavior: 'smooth' });
    retire();
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.setTimeout(function () {
    if (done || window.pageYOffset > 24) { return; }
    if (document.documentElement.scrollHeight <= window.innerHeight + 80) { return; }
    cue.classList.add('is-on');
  }, 1200);
}());

/* Rudi: "when they click view male collection it should scroll down to the
   banner of the For him, and the same with the female collection." Any link
   on the page that points at an id scrolls there smoothly, clearing the
   theme's sticky header so the banner is not hidden under it. */
(function () {
  function headerOffset() {
    var best = 0;
    var els = document.querySelectorAll('header, .header-wrapper, [class*="section-header"]');
    Array.prototype.forEach.call(els, function (el) {
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') { return; }
      var r = el.getBoundingClientRect();
      if (r.top > 4 || r.height < 20) { return; }
      if (r.height > best) { best = r.height; }
    });
    return best ? Math.round(best) + 8 : 0;
  }

  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
    if (!a) { return; }
    var id = a.getAttribute('href').slice(1);
    if (!id) { return; }
    var target = document.getElementById(id);
    if (!target) { return; }
    e.preventDefault();
    var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'smooth' });
    if (history.replaceState) { history.replaceState(null, '', '#' + id); }
  });
}());
